/**
 * Display formatting for `type: "number"` table/card/board fields.
 *
 * Before this existed, numbers had no case in the cell renderer and fell
 * through to `String(value)`, so money read as "25000" and a probability as
 * "40" everywhere a deal was shown — most visibly on the pipeline board, whose
 * cards printed raw field values.
 *
 * Formatting is OPT-IN via `SimpleFieldConfig.numberFormat` (with a
 * `DollarSign` icon as a legacy money hint) because some number fields are
 * identifiers — `ticket_number` must stay "10500", never "10,500".
 */

import { formatCurrency } from "@west-tennessee-consulting/ui";

import type { SimpleFieldConfig } from "@/components/shared/UniversalCRMTable/types/index";

export type NumberFieldFormat = NonNullable<SimpleFieldConfig["numberFormat"]>;

/**
 * The format to use for a field, or `null` when the value should render
 * verbatim (the pre-existing behavior for un-annotated number fields).
 */
export function resolveNumberFieldFormat(
  fieldConfig: Pick<SimpleFieldConfig, "numberFormat" | "icon"> | undefined
): NumberFieldFormat | null {
  if (!fieldConfig) return null;
  if (fieldConfig.numberFormat) return fieldConfig.numberFormat;
  // Legacy hint: money columns were already marked with a dollar icon long
  // before `numberFormat` existed, so honor it rather than requiring every
  // config to be touched at once.
  if (fieldConfig.icon === "DollarSign") return "currency";
  return null;
}

/** Coerce a cell value to a finite number, or null when it isn't one. */
export function toFiniteNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value === "string") {
    // Strip currency symbols/commas a legacy text value may carry.
    const cleaned = value.replace(/[$,\s]/g, "");
    if (cleaned === "" || cleaned === "-") return null;
    const parsed = Number(cleaned);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

/**
 * Format money. Whole amounts drop the ".00" so a board column reads
 * `$25,000` rather than `$25,000.00`; fractional amounts keep cents.
 */
export function formatCurrencyValue(amount: number, currency = "USD"): string {
  const digits = Math.abs(amount % 1) > 0.0001 ? 2 : 0;
  return formatCurrency(amount, {
    currency,
    // Callers hand this a value already narrowed by `toFiniteNumber`, so the
    // fallback is unreachable in practice — it exists so the return type stays
    // `string` instead of leaking `null` into every cell renderer.
    fallback: "",
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

/**
 * Compact money for tight spaces (board column headers): `$1.2M`, `$25k`.
 * Falls back to the full format below $10,000, where the exact number still
 * fits and rounding would hide meaningful precision.
 */
export function formatCompactCurrencyValue(amount: number, currency = "USD"): string {
  if (Math.abs(amount) < 10_000) return formatCurrencyValue(amount, currency);
  // Deliberately NOT the canonical `formatCurrency`: compact notation is the
  // whole point of this function and `FormatCurrencyOptions` has no `notation`
  // passthrough. Baselined in no-handrolled-currency.baseline.json with that
  // reason rather than widening the shared helper for one board header.
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    notation: "compact",
    // minimumFractionDigits must be pinned to 0: compact notation otherwise
    // keeps a trailing zero and renders "$40.0K" instead of "$40K".
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  }).format(amount);
}

/**
 * Render a number field for display. Returns null when the field has no
 * declared format (caller keeps its previous behavior) or when the value is
 * not a number.
 */
export function formatNumberField(
  value: unknown,
  fieldConfig: Pick<SimpleFieldConfig, "numberFormat" | "icon"> | undefined
): string | null {
  const format = resolveNumberFieldFormat(fieldConfig);
  if (!format) return null;

  const numeric = toFiniteNumber(value);
  if (numeric === null) return null;

  switch (format) {
    case "currency":
      return formatCurrencyValue(numeric);
    case "percent":
      return `${new Intl.NumberFormat("en-US", { maximumFractionDigits: 1 }).format(numeric)}%`;
    case "integer":
      return new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(numeric);
    case "decimal":
      return new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 }).format(numeric);
  }
}

/**
 * Sum a numeric field across rows, skipping non-numeric values. Used for
 * pipeline-board column totals — a stage header that shows only a card count
 * cannot answer "how much is in Proposal?".
 */
export function sumNumberField<T>(rows: readonly T[], field: keyof T & string): number {
  let total = 0;
  for (const row of rows) {
    const numeric = toFiniteNumber((row as Record<string, unknown>)[field]);
    if (numeric !== null) total += numeric;
  }
  return total;
}
